/**
 * ArgenLinksAdmin — Links compartibles para Argentina
 *
 * Panel para generar y copiar los links directos que muestran
 * BioTemplatePremiumSimple a usuarios argentinos (MercadoPago).
 *
 * No está en el navbar público ni en el sitemap.
 * Compartir via Stories / DMs / Bio de Instagram.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { BRAND_SITE_URL } from "@/lib/brandConfig";
import {
  Check,
  Copy,
  ExternalLink,
  Flag,
  AlertTriangle,
  Info,
  Link2,
  Loader2,
  Plus,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

// ─── Env vars leídas en build (Vite) ─────────────────────────────────────────

const MP_CHECKOUT_BASIC   = import.meta.env.VITE_MP_CHECKOUT_BASIC_AR?.trim() || "";
const MP_CHECKOUT_VIP     = import.meta.env.VITE_MP_CHECKOUT_VIP_AR?.trim()   || "";
const TELEGRAM_CTA_BASIC  = import.meta.env.VITE_MUCHAS_GRACIAS_CTA_URL_BASIC?.trim() || "";
const TELEGRAM_CTA_VIP    = import.meta.env.VITE_MUCHAS_GRACIAS_CTA_URL_VIP?.trim()   || "";
const AR_VIP_PRICE        = import.meta.env.VITE_PREMIUM_ARS_VIP_PRICE?.trim()       || "";
const AR_STD_PRICE        = import.meta.env.VITE_PREMIUM_ARS_STANDARD_PRICE?.trim()  || "";
const WA_BROADCAST_PRICE  = import.meta.env.VITE_WHATSAPP_BROADCAST_PRICE_ARS?.trim() || "";

// ─── Admin token helper ───────────────────────────────────────────────────────

function getAdminToken(): string | null {
  return (
    (import.meta.env.VITE_ADMIN_DASHBOARD_API_KEY as string | undefined) ||
    localStorage.getItem("auth_token") ||
    sessionStorage.getItem("auth_token") ||
    null
  );
}

const BASE = (BRAND_SITE_URL || "https://www.anaafernandez.com").replace(/\/$/, "");

// ─── Links directos (rutas que renderizan BioTemplatePremiumSimple) ───────────

const DIRECT_LINKS = [
  {
    slug: "/acceso",
    label: "Acceso",
    description: "Link genérico — ideal para bio de Instagram o linktree",
    recommended: true,
  },
  {
    slug: "/unete",
    label: "Únete",
    description: "Para campañas en stories en español",
    recommended: false,
  },
  {
    slug: "/vip",
    label: "VIP",
    description: "Para DMs directos o cierre de campaña",
    recommended: false,
  },
  {
    slug: "/invitacion-premium",
    label: "Invitación Premium",
    description: "URL original — funciona igual para AR",
    recommended: false,
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function useCopy(timeoutMs = 2000) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), timeoutMs);
    } catch {
      // clipboard API unavailable
    }
  };
  return { copied, copy };
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      className={cn(
        "inline-block h-2 w-2 rounded-full shrink-0",
        ok ? "bg-emerald-500" : "bg-amber-500",
      )}
    />
  );
}

function CopyButton({
  text,
  id,
  copied,
  onCopy,
}: {
  text: string;
  id: string;
  copied: string | null;
  onCopy: (text: string, key: string) => void;
}) {
  const isCopied = copied === id;
  return (
    <button
      onClick={() => onCopy(text, id)}
      disabled={!text}
      className={cn(
        "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all duration-150",
        "border shrink-0",
        text
          ? isCopied
            ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-400"
            : "border-border/40 bg-white/5 text-muted-foreground hover:border-primary/40 hover:bg-primary/10 hover:text-foreground active:scale-95"
          : "border-border/20 bg-white/5 text-muted-foreground/40 cursor-not-allowed",
      )}
    >
      {isCopied ? (
        <>
          <Check className="h-3 w-3" /> Copiado
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" /> Copiar
        </>
      )}
    </button>
  );
}

// ─── Crear suscripción personalizada ─────────────────────────────────────────

/**
 * Crea una preapproval (instancia de suscripción) en MercadoPago para una persona
 * específica. Devuelve un init_point personalizado para enviarle por DM.
 *
 * Diferencia clave:
 *   preapproval_plan  → el "producto" genérico (cualquiera puede suscribirse)
 *   preapproval       → una suscripción para UNA persona (link personalizado)
 */
function CreateSubscriptionPanel() {
  const { copied, copy } = useCopy();
  const [tier, setTier] = useState<"basic" | "vip">("vip");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    init_point: string;
    subscription_id: string;
  } | null>(null);

  const PLAN_OPTIONS = [
    {
      value: "basic" as const,
      label: "Basic",
      description: "Grupo Telegram",
      price: AR_STD_PRICE
        ? `$${Number(AR_STD_PRICE).toLocaleString("es-AR")} ARS/mes`
        : null,
      reason: "Membresía Basic Argentina",
    },
    {
      value: "vip" as const,
      label: "VIP",
      description: "Telegram + Mejores Amigos + Contenido exclusivo",
      price: AR_VIP_PRICE
        ? `$${Number(AR_VIP_PRICE).toLocaleString("es-AR")} ARS/mes`
        : null,
      reason: "Membresía VIP Argentina",
    },
  ];

  const selected = PLAN_OPTIONS.find((o) => o.value === tier)!;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const priceNum = tier === "vip"
        ? Number(AR_VIP_PRICE || 0)
        : Number(AR_STD_PRICE || 0);

      const res = await fetch("/api/mercadopago/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: selected.reason,
          // price en unidades ARS (no centavos — la API normaliza)
          price: priceNum > 0 ? priceNum : undefined,
          currency: "ARS",
          buyerEmail: email.trim() || undefined,
          skipPayerEmail: !email.trim(),
          metadata: {
            flow: "bio_template_premium",
            plan_tier: tier,
            source: "admin_argen_links",
            created_by: "admin",
          },
        }),
      });

      const data = (await res.json()) as {
        init_point?: string;
        subscription_id?: string | number;
        error?: string;
      };

      if (!res.ok || !data.init_point) {
        throw new Error(data.error || "No se pudo crear la suscripción en MercadoPago");
      }

      setResult({
        init_point: data.init_point,
        subscription_id: String(data.subscription_id || ""),
      });
      setEmail("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={handleCreate} className="space-y-4">
        {/* Plan selector */}
        <div>
          <p className="text-xs text-muted-foreground mb-2">Plan</p>
          <div className="grid grid-cols-2 gap-2">
            {PLAN_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setTier(opt.value);
                  setResult(null);
                  setError(null);
                }}
                className={cn(
                  "flex flex-col items-start p-3 rounded-lg border text-left transition-all duration-150",
                  tier === opt.value
                    ? "border-primary/50 bg-primary/10 ring-1 ring-primary/20"
                    : "border-border/20 bg-card/20 hover:border-border/40 hover:bg-card/40",
                )}
              >
                <span className="text-sm font-semibold text-foreground">
                  {opt.label}
                </span>
                <span className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  {opt.description}
                </span>
                {opt.price && (
                  <span className="text-xs text-primary/80 font-medium mt-1">
                    {opt.price}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">
            Email del suscriptor{" "}
            <span className="text-muted-foreground/50">(recomendado)</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seguidora@gmail.com"
            className={cn(
              "w-full h-9 rounded-lg border bg-card/30 px-3 text-sm",
              "placeholder:text-muted-foreground/40",
              "focus:outline-none focus:ring-1 focus:ring-primary/50",
              "border-border/30 text-foreground",
            )}
          />
          <p className="text-xs text-muted-foreground/50 mt-1">
            Con email, MercadoPago asocia la suscripción a esa cuenta.
            Sin email, el suscriptor lo ingresa en el checkout.
          </p>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={cn(
            "w-full flex items-center justify-center gap-2 h-10 rounded-lg",
            "bg-primary text-primary-foreground text-sm font-semibold",
            "hover:bg-primary/90 disabled:opacity-50 transition-colors",
          )}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Crear suscripción en MercadoPago
        </button>
      </form>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {result && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-400 shrink-0" />
            <span className="text-sm font-semibold text-emerald-300">
              Suscripción creada en MercadoPago
            </span>
            {result.subscription_id && (
              <code className="text-[10px] text-emerald-300/50 ml-auto font-mono">
                {result.subscription_id}
              </code>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">
              Link de pago — copiar y enviar por DM:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 text-xs text-foreground/70 break-all bg-black/30 px-2.5 py-2 rounded-lg border border-border/20 leading-relaxed">
                {result.init_point}
              </code>
            </div>
            <div className="flex gap-2">
              <a
                href={result.init_point}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center gap-1.5 rounded-lg border border-border/40 bg-white/5",
                  "px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground",
                )}
              >
                <ExternalLink className="h-3 w-3" />
                Abrir
              </a>
              <CopyButton
                text={result.init_point}
                id="subscription-init-point"
                copied={copied}
                onCopy={copy}
              />
            </div>
          </div>

          <p className="text-xs text-muted-foreground/50 pt-1 border-t border-border/20">
            La persona hace clic en el link → completa el pago en MercadoPago →
            la suscripción queda activa. El webhook la registra automáticamente.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── WhatsApp Broadcast — crear preapproval_plan mensual ─────────────────────

/**
 * Crea un preapproval_plan (membresía mensual genérica) en MercadoPago
 * para el canal de WhatsApp Broadcast.
 *
 * Diferencia clave con CreateSubscriptionPanel:
 *   preapproval_plan → plan genérico; cualquier suscriptora usa el mismo link.
 *   preapproval      → link personalizado para una persona específica.
 *
 * El link resultante se envía por DM a las suscriptoras que se registraron
 * en /registro?from_whatsapp=1.
 */
function WhatsAppBroadcastPlanPanel() {
  const { copied, copy } = useCopy();
  const [reason, setReason] = useState("Canal WhatsApp Broadcast");
  const [amount, setAmount] = useState(WA_BROADCAST_PRICE || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ id: string; init_point: string } | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getAdminToken();
    if (!token) {
      setError("Sin API key de admin. Configurá VITE_ADMIN_DASHBOARD_API_KEY o iniciá sesión.");
      return;
    }
    const amountNum = parseFloat(amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setError("Monto inválido — debe ser mayor a 0 ARS");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch("/api/mercadopago/create-preapproval-plan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason: reason.trim() || "Canal WhatsApp Broadcast",
          amount: amountNum,
          currency: "ARS",
          frequency: 1,
          frequencyType: "months",
          metadata: {
            tier: "basic",
            flow: "whatsapp_broadcast",
            source: "admin_argen_links",
          },
        }),
      });

      const data = (await res.json()) as {
        id?: string;
        init_point?: string;
        error?: string;
      };

      if (!res.ok || !data.id) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      setResult({ id: data.id, init_point: data.init_point ?? "" });
      setAmount("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={handleCreate} className="space-y-4">
        {/* Reason / nombre del plan */}
        <div>
          <p className="text-xs text-muted-foreground mb-1.5">
            Nombre del plan{" "}
            <span className="text-muted-foreground/50">(aparece en el checkout de MP)</span>
          </p>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Canal WhatsApp Broadcast"
            required
            className={cn(
              "w-full h-9 rounded-lg border bg-card/30 px-3 text-sm",
              "placeholder:text-muted-foreground/40 border-border/30 text-foreground",
              "focus:outline-none focus:ring-1 focus:ring-green-500/50",
            )}
          />
        </div>

        {/* Precio ARS */}
        <div>
          <p className="text-xs text-muted-foreground mb-1.5">Precio en ARS / mes *</p>
          <input
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={WA_BROADCAST_PRICE || "6999"}
            required
            className={cn(
              "w-full h-9 rounded-lg border bg-card/30 px-3 text-sm",
              "placeholder:text-muted-foreground/40 border-border/30 text-foreground",
              "focus:outline-none focus:ring-1 focus:ring-green-500/50",
            )}
          />
          {WA_BROADCAST_PRICE && (
            <p className="text-xs text-muted-foreground/50 mt-1">
              Default desde env:{" "}
              <code>VITE_WHATSAPP_BROADCAST_PRICE_ARS = {WA_BROADCAST_PRICE}</code>
            </p>
          )}
        </div>

        {/* Info */}
        <div className="flex items-start gap-2 rounded-lg border border-border/15 bg-white/3 p-3">
          <Info className="h-3.5 w-3.5 text-muted-foreground/60 mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground/70 leading-relaxed">
            Se crea un <code>preapproval_plan</code> mensual en MercadoPago. El link resultante es{" "}
            <strong>genérico</strong> — cualquier suscriptora puede usarlo para pagar.
            Envialo por DM a cada persona que se registró en{" "}
            <code>/registro?from_whatsapp=1</code>.
          </p>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={cn(
            "w-full flex items-center justify-center gap-2 h-10 rounded-lg",
            "bg-green-700 text-white text-sm font-semibold",
            "hover:bg-green-600 disabled:opacity-50 transition-colors",
          )}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Crear plan mensual en MercadoPago
        </button>
      </form>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {result && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-400 shrink-0" />
            <span className="text-sm font-semibold text-emerald-300">
              Plan mensual creado en MercadoPago
            </span>
            <code className="text-[10px] text-emerald-300/50 ml-auto font-mono">
              {result.id}
            </code>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">
              Link de suscripción — copiar y enviar por DM a suscriptoras de WhatsApp:
            </p>
            <code className="block text-xs text-foreground/70 break-all bg-black/30 px-2.5 py-2 rounded-lg border border-border/20 leading-relaxed">
              {result.init_point}
            </code>
            <div className="flex gap-2">
              <a
                href={result.init_point}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center gap-1.5 rounded-lg border border-border/40 bg-white/5",
                  "px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground",
                )}
              >
                <ExternalLink className="h-3 w-3" />
                Abrir
              </a>
              <CopyButton
                text={result.init_point}
                id="wa-broadcast-plan"
                copied={copied}
                onCopy={copy}
              />
            </div>
          </div>

          <p className="text-xs text-muted-foreground/50 pt-1 border-t border-border/20">
            Este link es permanente y reutilizable — sirve para todas las suscriptoras futuras.
            Guardá el Plan ID (<code>{result.id}</code>) para vincularlo desde{" "}
            <strong>Planes de Suscripción</strong> si lo necesitás trackear en la DB.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Secciones ────────────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/60 mb-3">
      {children}
    </h2>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ArgenLinksAdmin() {
  const { copied, copy } = useCopy();

  const missingEnvs = [
    !MP_CHECKOUT_BASIC && "VITE_MP_CHECKOUT_BASIC_AR",
    !MP_CHECKOUT_VIP   && "VITE_MP_CHECKOUT_VIP_AR",
    !TELEGRAM_CTA_BASIC && "VITE_MUCHAS_GRACIAS_CTA_URL_BASIC",
    !TELEGRAM_CTA_VIP   && "VITE_MUCHAS_GRACIAS_CTA_URL_VIP",
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-8">
      <AdminPageHeader
        description={
          <>
            Links compartibles AR (Stories / DM) hacia checkout MercadoPago.
            Config DualCTA país AR → Home por país.
          </>
        }
      />

      {/* ── Banner de warnings ─────────────────────────────────────────────── */}
      {missingEnvs.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-300 mb-1">
                Variables de entorno faltantes
              </p>
              <p className="text-xs text-amber-300/70 mb-2">
                Los links de landing funcionan, pero el checkout o el destino post-pago
                puede fallar si no están configuradas estas vars en Railway:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {missingEnvs.map((key) => (
                  <code
                    key={key}
                    className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-300"
                  >
                    {key}
                  </code>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Links directos (landing) ───────────────────────────────────────── */}
      <div>
        <SectionTitle>Links de landing — compartir a seguidoras AR</SectionTitle>
        <div className="space-y-2">
          {DIRECT_LINKS.map((link) => {
            const full = `${BASE}${link.slug}`;
            return (
              <div
                key={link.slug}
                className={cn(
                  "flex items-center gap-3 rounded-xl border p-4 transition-colors",
                  link.recommended
                    ? "border-primary/30 bg-primary/8"
                    : "border-border/20 bg-card/30",
                )}
              >
                <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-foreground">
                      {link.label}
                    </span>
                    {link.recommended && (
                      <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                        recomendado
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    {link.description}
                  </p>
                  <code className="text-xs text-foreground/60 break-all">
                    {full}
                  </code>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <a
                    href={full}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded-lg border border-border/40 bg-white/5 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-border/60 hover:text-foreground"
                    title="Abrir"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <CopyButton
                    text={full}
                    id={`link-${link.slug}`}
                    copied={copied}
                    onCopy={copy}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex items-start gap-2 rounded-lg border border-border/15 bg-white/3 p-3">
          <Info className="h-3.5 w-3.5 text-muted-foreground/60 mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground/60">
            Todos estos links muestran <strong className="text-muted-foreground">BioTemplatePremiumSimple</strong>.
            Cloudflare detecta el país por IP y decide el método de pago automáticamente
            (MercadoPago para AR, Stripe para el resto).
          </p>
        </div>
      </div>

      {/* ── Checkouts MercadoPago ──────────────────────────────────────────── */}
      <div>
        <SectionTitle>Checkouts MercadoPago (AR)</SectionTitle>
        <div className="space-y-2">
          {(
            [
              {
                key: "MP_CHECKOUT_BASIC",
                label: "Checkout Basic",
                value: MP_CHECKOUT_BASIC,
                price: AR_STD_PRICE ? `$${Number(AR_STD_PRICE).toLocaleString("es-AR")} ARS` : null,
                envVar: "VITE_MP_CHECKOUT_BASIC_AR",
              },
              {
                key: "MP_CHECKOUT_VIP",
                label: "Checkout VIP",
                value: MP_CHECKOUT_VIP,
                price: AR_VIP_PRICE ? `$${Number(AR_VIP_PRICE).toLocaleString("es-AR")} ARS` : null,
                envVar: "VITE_MP_CHECKOUT_VIP_AR",
              },
            ] as const
          ).map((row) => (
            <div
              key={row.key}
              className="flex items-center gap-3 rounded-xl border border-border/20 bg-card/30 p-4"
            >
              <StatusDot ok={Boolean(row.value)} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-foreground">
                    {row.label}
                  </span>
                  {row.price && (
                    <span className="text-xs text-muted-foreground">
                      {row.price}
                    </span>
                  )}
                </div>
                <code className="text-xs text-muted-foreground/60 break-all">
                  {row.value || (
                    <span className="text-amber-400/70">
                      No configurado — agregar <strong>{row.envVar}</strong> en Railway
                    </span>
                  )}
                </code>
              </div>
              {row.value && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <a
                    href={row.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded-lg border border-border/40 bg-white/5 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-border/60 hover:text-foreground"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <CopyButton
                    text={row.value}
                    id={row.key}
                    copied={copied}
                    onCopy={copy}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Crear suscripción personalizada ───────────────────────────────────── */}
      <div>
        <SectionTitle>Crear suscripción personalizada 🇦🇷</SectionTitle>
        <div className="rounded-xl border border-border/20 bg-card/30 p-5">
          <p className="text-xs text-muted-foreground mb-5">
            Generá un link de pago MercadoPago para una persona específica.
            La suscripción se registra directamente en MP — enviá el link por DM.
          </p>
          <CreateSubscriptionPanel />
        </div>
      </div>

      {/* ── WhatsApp Broadcast — Plan mensual (preapproval_plan) ─────────── */}
      <div>
        <SectionTitle>WhatsApp Broadcast — Plan mensual 🟢</SectionTitle>
        <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-5">
          <p className="text-xs text-muted-foreground mb-5">
            Creá el plan de membresía mensual en MercadoPago para el canal de WhatsApp Broadcast.
            A diferencia de la suscripción personalizada, este es un{" "}
            <strong>plan genérico</strong> que genera un link único y permanente — enviáselo por
            DM a cada seguidora que se registró en{" "}
            <code className="text-xs bg-black/20 px-1 rounded">/registro?from_whatsapp=1</code>.
          </p>
          <WhatsAppBroadcastPlanPanel />
        </div>
      </div>

      {/* ── Destino post-pago (Telegram) ───────────────────────────────────── */}
      <div>
        <SectionTitle>Destino post-pago — Telegram</SectionTitle>
        <div className="space-y-2">
          {(
            [
              {
                key: "TG_BASIC",
                label: "Grupo Basic",
                value: TELEGRAM_CTA_BASIC,
                envVar: "VITE_MUCHAS_GRACIAS_CTA_URL_BASIC",
              },
              {
                key: "TG_VIP",
                label: "Grupo VIP",
                value: TELEGRAM_CTA_VIP,
                envVar: "VITE_MUCHAS_GRACIAS_CTA_URL_VIP",
              },
            ] as const
          ).map((row) => (
            <div
              key={row.key}
              className="flex items-center gap-3 rounded-xl border border-border/20 bg-card/30 p-4"
            >
              <StatusDot ok={Boolean(row.value)} />
              <div className="min-w-0 flex-1">
                <span className="text-sm font-semibold text-foreground block mb-0.5">
                  {row.label}
                </span>
                <code className="text-xs text-muted-foreground/60 break-all">
                  {row.value || (
                    <span className="text-amber-400/70">
                      No configurado — agregar <strong>{row.envVar}</strong> en Railway
                    </span>
                  )}
                </code>
              </div>
              {row.value && (
                <CopyButton
                  text={row.value}
                  id={row.key}
                  copied={copied}
                  onCopy={copy}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Flujo completo ────────────────────────────────────────────────── */}
      <div>
        <SectionTitle>Flujo completo para Argentina</SectionTitle>
        <div className="rounded-xl border border-border/20 bg-card/20 p-5">
          <ol className="space-y-3">
            {[
              {
                step: "1",
                text: "Seguidora argentina hace clic en el link compartido",
                detail: `Ej: ${BASE}/acceso`,
              },
              {
                step: "2",
                text: "Cloudflare Worker detecta AR por IP → inyecta country=AR",
                detail: "window.__TEMPLATE_CONFIG__ = { country: 'AR', ... }",
              },
              {
                step: "3",
                text: "Ve BioTemplatePremiumSimple con botones de MercadoPago",
                detail: "Basic → VITE_MP_CHECKOUT_BASIC_AR  |  VIP → VITE_MP_CHECKOUT_VIP_AR",
              },
              {
                step: "4",
                text: "Paga en MercadoPago → redirige a /muchas-gracias?preapproval_id=XXX",
                detail: "validate-subscription verifica el pago con MP API",
              },
              {
                step: "5",
                text: "Si valid=true → muestra el link al grupo de Telegram",
                detail: "VITE_MUCHAS_GRACIAS_CTA_URL_BASIC o _VIP según el plan",
              },
            ].map(({ step, text, detail }) => (
              <li key={step} className="flex items-start gap-3">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary mt-0.5">
                  {step}
                </span>
                <div>
                  <p className="text-sm text-foreground">{text}</p>
                  <code className="text-[11px] text-muted-foreground/60">{detail}</code>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* ── Sin Cloudflare (bypass) ───────────────────────────────────────── */}
      <div>
        <SectionTitle>¿Qué pasa si Cloudflare no está activo?</SectionTitle>
        <div className="rounded-xl border border-border/15 bg-white/3 p-4">
          <div className="flex items-start gap-3">
            <Flag className="h-4 w-4 text-muted-foreground/60 mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground/70 leading-relaxed">
              Si alguien accede por un dominio de preview (<code className="text-muted-foreground">*.up.railway.app</code>) o
              sin pasar por Cloudflare, <code className="text-muted-foreground">window.__TEMPLATE_CONFIG__</code> no existe.
              El país queda como <code className="text-muted-foreground">UNKNOWN</code> →
              la página muestra Stripe (USD) en lugar de MercadoPago.
              Las URLs de landing siguen funcionando — solo cambia el método de pago al de Stripe.
              Esto es correcto como fallback seguro para usuarios sin AR detectado.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
